import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Modal, ModalBody } from "reactstrap";
import CreateCommunity from "../../../components/Community/AddCommunity";
import All from "./All";
import { MdClose, MdArchive } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import "../../../assets/scss/custom/pages/_dashboard.scss";
import PropTypes from "prop-types";

const ShowEvents = ({ t, userId, userEmail, token, userRole }) => {
  // 🔥 GUARANTEED DEBUG LOGS
  useEffect(() => {
    console.log('🚨 ShowEvents component MOUNTED!');
    console.log('🔍 Props received:', { 
      userId: userId ? '✅' : '❌', 
      userEmail: userEmail ? '✅' : '❌', 
      token: token ? '✅ (prop)' : '❌ (prop)',
      userRole 
    });
  }, []);

  const [search, setSearch] = useState("");
  const [isSwitchOn, setIsSwitchOn] = useState(true);
  const [modal, setModal] = useState(false);
  const [showArchives, setShowArchives] = useState(false);
  
  // ⏱️ SESSION TIMEOUT STATE
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const idleTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  
  const navigate = useNavigate();

  const handleSearch = (event) => setSearch(event.target.value);
  const handleCreateCommunity = () => setModal(!modal);
  const handleToggleArchives = () => setShowArchives(!showArchives);

  // ===== SESSION TIMEOUT LOGIC =====
  useEffect(() => {
    console.log('⏱️ Session timeout check - token prop exists:', !!token);
    
    // ✅ Use token PROP instead of localStorage
    if (!token) {
      console.warn('⚠️ Session timeout: No token prop - skipping');
      return;
    }

    // ⏱️ PRODUCTION CONFIG: 15 minutes total (13m idle + 2m warning)
    const IDLE_TIMEOUT = 10 * 60 * 1000;  // 15 minutes (900000ms)
    const WARNING_TIME = 2 * 60 * 1000;    // 2 minute warning (120000ms)
    
    console.log(`✅ Session timeout started - Idle: ${IDLE_TIMEOUT/1000/60}min, Warning: ${WARNING_TIME/1000/60}min`);

    const resetIdleTimer = () => {
      // ⚠️ IMPORTANT: Only reset if warning is NOT showing
      // Once warning appears, mouse movement should NOT dismiss it
      if (showSessionWarning) {
        return; // Don't reset timers while warning is active
      }

      clearTimeout(idleTimerRef.current);
      clearTimeout(warningTimerRef.current);

      // Start warning timer (show warning after idle period)
      warningTimerRef.current = setTimeout(() => {
        console.log('⚠️ Warning: Session expiring in 2 minutes!');
        setShowSessionWarning(true);

        // Force logout after warning period (user must click a button)
        idleTimerRef.current = setTimeout(() => {
          console.log('🚪 Auto-logging out after warning period...');
          performLogout();
        }, WARNING_TIME);
      }, IDLE_TIMEOUT - WARNING_TIME);
    };

    const performLogout = () => {
      console.log('🚪 Logging out user...');
      // Clear localStorage just in case
      const keys = ['token', 'authToken', 'accessToken', 'user'];
      keys.forEach(key => localStorage.removeItem(key));
      
      navigate('/login', { 
        replace: true, 
        state: { reason: 'session_expired' } 
      });
    };

    // Listen for user activity (only resets timer if warning is not showing)
    const events = ['mousedown', 'keydown', 'scroll', 'mousemove', 'click', 'touchstart'];
    events.forEach(event => window.addEventListener(event, resetIdleTimer, { passive: true }));

    // Start initial timer
    resetIdleTimer();

    // Cleanup
    return () => {
      console.log('🧹 Session timeout: Cleaning up listeners and timers');
      events.forEach(event => window.removeEventListener(event, resetIdleTimer));
      clearTimeout(idleTimerRef.current);
      clearTimeout(warningTimerRef.current);
    };
  }, [navigate, token, showSessionWarning]); // ✅ Add showSessionWarning as dependency
  // ===== END SESSION TIMEOUT =====

  // ===== DEBUG: Check what role is actually coming in =====
  useEffect(() => {
    console.log("=== DEBUG ROLES ===");
    console.log("userRole Type:", typeof userRole);
    console.log("userRole Value:", userRole);
    console.log("Is Array:", Array.isArray(userRole));
    console.log("===================");
  }, [userRole]);

  // ===== SAFE ROLE CHECK =====
  const isOrganizer = true; // <--- FORCED TRUE FOR DEBUGGING

  // ===== MOBILE CARD SPACING FIX =====
  useEffect(() => {
    const fixCardSpacing = () => {
      if (window.innerWidth <= 768) {
        const selectors = [
          '.AllEvents-wrapper',
          '[class*="event-card"]',
          '[class*="card"]',
          '.MyEvent_image_div',
          '.card-body',
          '.p-3',
          '.p-4'
        ];

        selectors.forEach(selector => {
          const elements = document.querySelectorAll(selector);
          elements.forEach(el => {
            const imgs = el.querySelectorAll('img, .card-img-top');
            imgs.forEach(img => {
              img.style.marginBottom = '0';
              img.style.paddingBottom = '0';
              img.style.display = 'block';
            });

            if (el.classList.contains('card-body') || 
                el.classList.contains('p-3') || 
                el.classList.contains('p-4')) {
              el.style.paddingTop = '4px';
              el.style.marginTop = '0';
            }

            const headings = el.querySelectorAll('h2, h3, h4, h5, h6, .card-title, .event-title, [class*="title"]');
            headings.forEach(h => {
              h.style.marginTop = '0';
              h.style.paddingTop = '0';
              h.style.marginBottom = '8px';
              h.style.display = 'block';
            });
          });
        });
      }
    };

    fixCardSpacing();
    const t1 = setTimeout(fixCardSpacing, 100);
    const t2 = setTimeout(fixCardSpacing, 300);
    const t3 = setTimeout(fixCardSpacing, 600);

    window.addEventListener('resize', fixCardSpacing);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      window.removeEventListener('resize', fixCardSpacing);
    };
  }, []);

  // ===== SESSION TIMEOUT WARNING MODAL =====
  const handleStayLoggedIn = () => {
    console.log('🖱️ User clicked "Stay Logged In"');
    setShowSessionWarning(false);
    // Reset the idle timer to start fresh
    // (The useEffect will re-run and start new timers due to showSessionWarning dependency)
  };

  const handleLogoutNow = () => {
    console.log('🚪 User clicked "Log Out Now"');
    setShowSessionWarning(false);
    const keys = ['token', 'authToken', 'accessToken', 'user'];
    keys.forEach(key => localStorage.removeItem(key));
    navigate('/login', { replace: true });
  };

  return (
    <div className="dashboard-container px-3 px-md-4">
      
      {/* ⏱️ SESSION TIMEOUT WARNING MODAL - STAYS UNTIL USER CLICKS */}
      {showSessionWarning && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
        }}>
          <div style={{
            background: '#fff', padding: '40px 32px', borderRadius: '16px',
            maxWidth: '450px', width: '90%', textAlign: 'center',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            animation: 'slideIn 0.3s ease-out'
          }}>
            <div style={{
              width: '80px', height: '80px', background: '#fff7ed',
              borderRadius: '50%', display: 'flex', alignItems: 'center',
              justifyContent: 'center', margin: '0 auto 24px'
            }}>
              <span style={{ fontSize: '40px' }}>⏰</span>
            </div>
            
            <h2 style={{ margin: '0 0 12px', color: '#1e293b', fontSize: '24px', fontWeight: '700' }}>
              Session Expiring Soon
            </h2>
            
            <p style={{ color: '#64748b', marginBottom: '32px', fontSize: '16px', lineHeight: 1.5 }}>
              For your security, you'll be logged out in <strong style={{ color: '#f97316' }}>2 minutes</strong> due to inactivity.
            </p>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              {/* ✅ TWO BUTTONS AS REQUESTED */}
              <button
                onClick={handleLogoutNow}
                style={{
                  padding: '12px 24px', background: '#f1f5f9', border: '1px solid #e2e8f0',
                  borderRadius: '8px', cursor: 'pointer', color: '#475569',
                  fontWeight: '600', fontSize: '14px', transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = '#e2e8f0'}
                onMouseOut={(e) => e.currentTarget.style.background = '#f1f5f9'}
              >
                Log Out Now
              </button>
              
              <button
                onClick={handleStayLoggedIn}
                style={{
                  padding: '12px 24px', background: 'linear-gradient(135deg, #FF7A00 0%, #ff8560f0 100%)',
                  border: 'none', borderRadius: '8px', cursor: 'pointer', color: '#fff',
                  fontWeight: '600', fontSize: '14px', boxShadow: '0 4px 14px rgba(255, 122, 0, 0.4)',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 122, 0, 0.5)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 14px rgba(255, 122, 0, 0.4)';
                }}
              >
                Stay Logged In
              </button>
            </div>
          </div>
          
          <style>{`
            @keyframes slideIn {
              from { opacity: 0; transform: translateY(-20px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>
        </div>
      )}

      <Row className="g-3 g-md-4">
        <Col xs={12}>
          {/* ===== HEADER SECTION ===== */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-3 mb-md-4 gap-3 w-100">
            
            {/* Left: Heading & Badge */}
            <div className="d-flex align-items-center gap-2 flex-shrink-0">
              <h4 className="m-0 fw-bold description-text" style={{ color: '#F68A20', fontSize: '20px' }}>
                {showArchives ? "Archived Events" : "My Events"}
              </h4>
              
              {showArchives && (
                <span className="badge bg-warning text-dark d-inline-flex align-items-center gap-1" style={{ fontSize: '12px', padding: '6px 12px', borderRadius: '20px' }}>
                  <MdArchive size={14} /> Archived
                </span>
              )}
            </div>
            
            {/* Right: Buttons Container */}
            <div className="d-flex align-items-center gap-2 flex-shrink-0 flex-wrap justify-content-center justify-content-md-end">
              
              {/* ARCHIVES BUTTON */}
              {isOrganizer && (
                <button
                  className={`btn-archives ${showArchives ? 'active' : ''}`}
                  onClick={handleToggleArchives}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '10px 20px',
                    borderRadius: '50px',
                    border: '2px solid #6c757d',
                    backgroundColor: showArchives ? '#6c757d' : 'transparent',
                    color: showArchives ? '#ffffff' : '#6c757d',
                    fontWeight: '600',
                    fontSize: '14px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    whiteSpace: 'nowrap',
                    minHeight: '44px',
                    flexShrink: 0
                  }}
                  onMouseOver={(e) => {
                    if (!showArchives) {
                      e.currentTarget.style.backgroundColor = '#6c757d';
                      e.currentTarget.style.color = '#ffffff';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!showArchives) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = '#6c757d';
                    }
                  }}
                  title={showArchives ? "View Active Events" : "View Archived Events"}
                >
                  <MdArchive size={18} />
                  <span>{showArchives ? "Active Events" : "Archives"}</span>
                </button>
              )}

              {/* CREATE EVENT BUTTON */}
              {isOrganizer && (
                <button
                  className="btn-create-event"
                  onClick={() => navigate("/create-event")}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '10px 24px',
                    borderRadius: '50px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #FF7A00 0%, #ff8560f0 100%)',
                    color: '#ffffff',
                    fontWeight: '600',
                    fontSize: '14px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    whiteSpace: 'nowrap',
                    minHeight: '44px',
                    flexShrink: 0,
                    boxShadow: '0 4px 14px rgba(255, 122, 0, 0.35)'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 122, 0, 0.5)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 14px rgba(255, 122, 0, 0.35)';
                  }}
                >
                  <span>Create Event</span>
                </button>
              )}

              {/* FALLBACK DEBUG MESSAGE */}
              {!isOrganizer && (
                <small className="text-muted fst-italic" style={{ fontSize: '12px' }}>
                  (Event Organizer access required)
                </small>
              )}
            </div>
          </div>

          {/* ===== EVENTS LIST ===== */}
          <div className="events-list-wrapper w-100">
            <All 
              t={t} 
              search={search} 
              isSwitchOn={isSwitchOn}
              showArchives={showArchives}
              userId={userId}
              userEmail={userEmail}
              token={token}
              userRole={userRole}
              mobileCardSpacing={true}
            />
          </div>
        </Col>
      </Row>

      {/* Community Creation Modal */}
      <Modal isOpen={modal} toggle={handleCreateCommunity} fullscreen>
        <ModalBody className="position-relative p-0">
          <div className="position-absolute top-0 end-0 p-3 z-index-1">
            <button 
              className="btn btn-link text-dark p-0 opacity-75" 
              onClick={handleCreateCommunity}
              aria-label="Close modal"
            >
              <MdClose size={24} />
            </button>
          </div>
          <div className="d-flex justify-content-center align-items-center min-vh-50 p-3">
            <CreateCommunity
              userId={userId}
              userEmail={userEmail}
              token={token}
              setModal={setModal}
            />
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
};

ShowEvents.propTypes = {
  t: PropTypes.func.isRequired,
  userId: PropTypes.string.isRequired,
  userEmail: PropTypes.string.isRequired,
  token: PropTypes.string.isRequired,
  userRole: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
};

export default ShowEvents;